//
// TableLayoutBuilder
//
// Based on view info from a layout

import { observer } from 'mobx-react-lite'
import { useContext } from 'react'
import { useStore } from '../../../../stores'

import { TypedField } from '.'
import { LazyScrollRow, LazyVScrollTable } from '../../../../appview'
import { Icon, Text } from '../../../../components'
import { useLazyScrollingRecordStore } from '../../../../hooks/useLazyScrollingRecordStore'
import { LAYOUTCOMPONENTTYPECONFIG } from '../../../../stores/data/LAYOUTCOMPONENTTYPECONFIG'
import { getAllDefinitionFieldGids } from '../../../../stores/data/utils'
import { FieldLabel, FieldMetadata } from '../fields'
import { LabeledField } from '../fields/LabeledField'
import { LocalizedContext } from './LocalizedContext'

export const TableLayoutBuilder = observer(function TableLayoutBuilder({
    className,
    component,
    components,
    layout,
    record,
    worksheet,
    active_class,
    visited_gids,
    force_readonly,
}) {
    const store = useStore()
    const { view } = useStore()
    const context_is_localized = useContext(LocalizedContext)

    const { children, totalsize, firstindex, onUpdate } = useLazyScrollingRecordStore(
        worksheet.record.gid,
        worksheet.children,
        worksheet.environment,
        0,
        10
    )

    const config = LAYOUTCOMPONENTTYPECONFIG['table']

    const name = component.name ? component.name : component.type
    const placeholder = component.placeholder
    const localizedPlaceholder = placeholder && placeholder[store.appLanguage]
    const editable = force_readonly
        ? false
        : 'editable' in component
          ? component.editable
          : config.editable.default
    const navigation =
        'navigation' in component ? component.navigation : config.navigation.default
    const height = component.height ? parseInt(component.height) : config.height.default
    const rowheight = component.rowheight
        ? parseInt(component.rowheight)
        : config.rowheight.default
    const headerheight = component.headerheight
        ? parseInt(component.headerheight)
        : config.headerheight.default

    let Table
    if (children && children.length) {
        const firstchild = children[0]
        const contentcomponents =
            component.content && component.content.length
                ? component.content.map(subelement_gid =>
                      components.get(subelement_gid)
                  )
                : []
        const fieldlikecomponents = contentcomponents.filter(
            cc => cc && ['field', 'fieldlist'].includes(cc.type)
        )
        let fieldnames = []
        let field_duckcomponents = {}
        const fieldlist_key = view.environment.get('fieldlist')
        fieldlikecomponents.map(fieldlikecomponent => {
            if (fieldlikecomponent.type === 'field') {
                fieldnames.push(fieldlikecomponent.field)
                field_duckcomponents[fieldlikecomponent.field] = {
                    editable:
                        editable &&
                        ('editable' in fieldlikecomponent
                            ? fieldlikecomponent.editable
                            : LAYOUTCOMPONENTTYPECONFIG['field'].editable.default),
                    style: fieldlikecomponent.style,
                    columns: fieldlikecomponent.columns,
                    metadata: fieldlikecomponent.metadata,
                }
            } else if (fieldlikecomponent.type === 'fieldlist') {
                // Merge fieldlist columns across all child definitions so mixed
                // extensions still share one table header.
                const merged = []
                children.forEach(child => {
                    const childdefinition = worksheet.definitions.get(child.definition)
                    const fieldlistnames =
                        childdefinition && childdefinition.fieldlists.has(fieldlist_key)
                            ? childdefinition.fieldlists.get(fieldlist_key)
                            : []
                    fieldlistnames.forEach(fieldgid => {
                        if (merged.includes(fieldgid)) return
                        merged.push(fieldgid)
                        field_duckcomponents[fieldgid] = {
                            editable:
                                editable &&
                                ('editable' in fieldlikecomponent
                                    ? fieldlikecomponent.editable
                                    : LAYOUTCOMPONENTTYPECONFIG['fieldlist'].editable
                                          .default),
                            style: fieldlikecomponent.style,
                            columns: fieldlikecomponent.columns,
                            metadata: fieldlikecomponent.metadata,
                            from_fieldlist: true,
                        }
                    })
                })
                fieldnames.push(...merged)
            }
            return true
        })

        const fields = worksheet.findFields(fieldnames)

        const firstHeadercell = navigation ? (
            <td className="ws-table-cell ws-table-cell-navigator"></td>
        ) : undefined
        const Headercells = fields.map((field, fieldindex) => {
            let classes = 'ws-table-cell ws-table-cell-' + field.name
            if (field.gid === worksheet.selected.property.fieldgid) {
                classes = classes + ' ws-selected'
            }
            const fieldcomponent =
                field.gid in field_duckcomponents
                    ? field_duckcomponents[field.gid]
                    : undefined
            let BelowLabel
            let BelowField
            if (fieldcomponent && fieldcomponent.metadata) {
                BelowLabel = [...fieldcomponent.metadata].map(
                    ([name, value], index) => {
                        return value === 'below_label' ? (
                            <div
                                key={name + '.' + index}
                                className="ws-metaslot ws-metaslot-below-label"
                            >
                                <FieldMetadata
                                    metadataname={name}
                                    metadatafield={field.metadata.get(name)}
                                    worksheet={worksheet}
                                />
                            </div>
                        ) : null
                    }
                )
                BelowField = [...fieldcomponent.metadata].map(
                    ([name, value], index) => {
                        return value === 'below_field' ? (
                            <div
                                key={name + '.' + index}
                                className="ws-metaslot ws-metaslot-below-field"
                            >
                                <FieldMetadata
                                    metadataname={name}
                                    metadatafield={field.metadata.get(name)}
                                    worksheet={worksheet}
                                />
                            </div>
                        ) : null
                    }
                )
            }

            return (
                <td
                    key={'field' + field.gid + ':' + fieldindex}
                    className={classes}
                    onClick={e => {
                        worksheet.setSelected(firstchild, null, field.gid, null, null)
                        e.preventDefault()
                        e.stopPropagation()
                        e.nativeEvent.stopImmediatePropagation()
                    }}
                >
                    <Text>
                        <FieldLabel
                            field={field}
                            inline
                            worksheet={worksheet}
                            metadata={fieldcomponent && fieldcomponent.metadata}
                        />
                        {context_is_localized || field.is_localized ? (
                            <span className="language">
                                {view.environment.get('language')}
                            </span>
                        ) : undefined}
                    </Text>
                    {BelowLabel}
                    {BelowField}
                </td>
            )
        })
        const Header = (
            <LazyScrollRow className="ws-table-header">
                {firstHeadercell}
                {Headercells}
            </LazyScrollRow>
        )

        const Rows = children.map((child, fieldindex) => {
            const childdefinition = worksheet.definitions.get(child.definition)
            const available_field_gids = getAllDefinitionFieldGids(
                childdefinition,
                store.data
            )
            const firstCell = navigation ? (
                <td
                    className="ws-table-cell ws-table-cell-navigator"
                    onClick={() => worksheet.setRecord(child)}
                >
                    <Icon name={'collapsed'} size={'text'} />
                </td>
            ) : undefined
            const Cells = fields.map((field, index) => {
                const field_available = available_field_gids.has(field.gid)
                const duck = field_duckcomponents[field.gid]
                const from_fieldlist = duck && duck.from_fieldlist
                let classes =
                    'ws-table-cell ws-table-cell-' + field.name + ' layout-component'
                if (field.gid in field_duckcomponents) {
                    classes += ' lc-' + field_duckcomponents[field.gid]['style']
                }
                if (from_fieldlist && !field_available) {
                    classes += ' cc-dimmed ws-table-cell-unavailable'
                }
                const editable =
                    field.gid in field_duckcomponents
                        ? field_duckcomponents[field.gid]['editable']
                        : false
                let Field
                if (from_fieldlist && !field_available) {
                    Field = null
                } else {
                    const value =
                        child.localized_fields &&
                        child.localized_fields.get('' + field.name)
                    Field =
                        value === undefined ? (
                            <Text>
                                {store.app.text("Undefined field '{fieldname}'", {
                                    fieldname: field.name,
                                })}
                            </Text>
                        ) : (
                            <TypedField
                                field={field}
                                fromClass={null}
                                component={field_duckcomponents[field.gid]}
                                editable={editable}
                                force_readonly={force_readonly}
                                record={child}
                                active_class={active_class}
                                worksheet={worksheet}
                                visited_gids={visited_gids}
                            />
                        )
                }
                return (
                    <td
                        key={'field' + field.gid + ':' + index}
                        className={classes}
                        onClick={e => {
                            worksheet.setSelected(child, null, field.gid, null, null)
                            e.preventDefault()
                            e.stopPropagation()
                            e.nativeEvent.stopImmediatePropagation()
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: rowheight,
                            }}
                            title={
                                field_available
                                    ? ''
                                    : store.app.text(
                                          "Field not available in record's definition"
                                      )
                            }
                        >
                            {Field}
                        </div>
                    </td>
                )
            })
            let rowclasses = 'ws-table-row status-' + child.status
            if (
                child.gid === worksheet.selected.record &&
                worksheet.selected.record.gid
            ) {
                rowclasses = rowclasses + ' ws-selected'
            }
            return (
                <LazyScrollRow
                    className={rowclasses}
                    key={child.gid + '/' + view.environment.get('fieldlist')}
                >
                    {firstCell}
                    {Cells}
                </LazyScrollRow>
            )
        })
        Table = (
            <LazyVScrollTable
                className="ws-record-navigator ws-rows"
                firstindex={firstindex}
                headerrow={Header}
                headerheight={headerheight}
                tableheight={height}
                rowheight={rowheight}
                totalitems={totalsize}
                onUpdate={onUpdate}
                renderkey={
                    worksheet.record.gid + '/' + view.environment.get('fieldlist')
                }
            >
                {Rows}
            </LazyVScrollTable>
        )
    }

    const Placeholder = localizedPlaceholder ? (
        <div className="ws-placeholder">{localizedPlaceholder}</div>
    ) : undefined

    let attributes = {
        className: 'layout-component ws-record-navigator ws-table',
    }
    if (className) attributes['className'] += ' ' + className
    if (component.gid === worksheet.layoutstore.selectedComponentGid) {
        attributes['className'] += ' cc-selected'
    }
    // attributes["onClick"] = e => onClickElement(e, component, layouttreestore)

    if (!Table) {
        attributes['className'] += ' ws-placeholder-visible'
        Table = (
            <div>
                <br />
            </div>
        )
    }

    return (
        <LabeledField
            fieldgid={name}
            editable={editable}
            hide_editable_icon={force_readonly}
            label={name}
            worksheet={worksheet}
            {...attributes}
        >
            {Table}
            {Placeholder}
        </LabeledField>
    )
})
